import { Grid, IconButton, Paper, Typography } from '@material-ui/core';
import SettingsIcon from '@material-ui/icons/Settings';
import { makeStyles } from '@material-ui/styles';
import React, { useState } from 'react';
import {
  withInventoryCalculationParametersHoc,
  WithInventoryCalculationParametersHocChildProps,
} from '../../../api/graphql/hoc/withInventoryCalculationParametersHoc';
import InventoryCalculationSettingsDialog, {
  PackFactorCalculationOption,
} from './InventoryCalculationSettingsDialog';

const useStyles = makeStyles({
  root: {
    padding: '8px 16px',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#F9FAFB',
    border: '1px solid #E5E7EB',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    width: '100%',
    minHeight: 64,
  },
  infoContainer: {
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  settingsButton: {
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    padding: 8,
    width: 36,
    height: 36,
    color: '#374151',
    backgroundColor: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#F3F4F6',
    },
  },
  spacer: {
    width: 36,
  },
  infoBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '0 24px',
  },
  label: {
    fontSize: 10,
    color: '#9CA3AF',
    textTransform: 'uppercase',
    fontWeight: 400,
    marginBottom: 4,
    letterSpacing: '0.05em',
    fontFamily: '"Source Sans Pro", sans-serif',
  },
  value: {
    fontSize: 16,
    color: '#333',
    fontWeight: 400,
    fontFamily: '"Source Sans Pro", sans-serif',
    letterSpacing: '0.15px',
  },
});

type Props = {
  grain_bin_id: number;
} & WithInventoryCalculationParametersHocChildProps;

const InventoryCalculationInfoBar: React.FC<Props> = ({
  grain_bin_id,
  inventory_calculation_parameters,
  loading,
}) => {
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = useState(false);

  if (loading || !inventory_calculation_parameters) {
    return null;
  }

  const {
    pack_factor_calculation_option,
    pack_factor_value,
    pack_factor_test_weight_lbsperbu,
    pack_factor_adjustment_index,
  } = inventory_calculation_parameters;

  const renderInfo = () => {
    switch (pack_factor_calculation_option) {
      case PackFactorCalculationOption.USDA_PACK_FACTOR:
        return (
          <>
            <div className={classes.infoBox}>
              <Typography className={classes.label}>Pack Table</Typography>
              <Typography className={classes.value}>USDA</Typography>
            </div>
            <div className={classes.infoBox}>
              <Typography className={classes.label}>Pack Factor</Typography>
              <Typography className={classes.value}>
                {pack_factor_value ? pack_factor_value.toFixed(3) : '0.000'}
              </Typography>
            </div>
            <div className={classes.infoBox}>
              <Typography className={classes.label}>Test Weight</Typography>
              <Typography className={classes.value}>
                {pack_factor_test_weight_lbsperbu
                  ? pack_factor_test_weight_lbsperbu.toFixed(1)
                  : '0.0'}
              </Typography>
            </div>
          </>
        );
      case PackFactorCalculationOption.CUSTOM_PACK_FACTOR_TABLE:
        return (
          <>
            <div className={classes.infoBox}>
              <Typography className={classes.label}>Pack Table</Typography>
              <Typography className={classes.value}>Custom</Typography>
            </div>
            <div className={classes.infoBox}>
              <Typography className={classes.label}>Pack Factor</Typography>
              <Typography className={classes.value}>
                {pack_factor_value ? pack_factor_value.toFixed(3) : '0.000'}
              </Typography>
            </div>
            <div className={classes.infoBox}>
              <Typography className={classes.label}>Test Weight</Typography>
              <Typography className={classes.value}>
                {pack_factor_test_weight_lbsperbu
                  ? pack_factor_test_weight_lbsperbu.toFixed(1)
                  : '0.0'}
              </Typography>
            </div>
          </>
        );
      case PackFactorCalculationOption.CUSTOM_PACK_FACTOR_WITH_ADJUSTMENT_INDEX:
        return (
          <>
            <div className={classes.infoBox}>
              <Typography className={classes.label}>Pack Table</Typography>
              <Typography className={classes.value}>Custom</Typography>
            </div>
            <div className={classes.infoBox}>
              <Typography className={classes.label}>Pack Adj. Index</Typography>
              <Typography className={classes.value}>
                {pack_factor_adjustment_index || '0'}
              </Typography>
            </div>
            <div className={classes.infoBox}>
              <Typography className={classes.label}>Test Weight</Typography>
              <Typography className={classes.value}>
                {pack_factor_test_weight_lbsperbu
                  ? pack_factor_test_weight_lbsperbu.toFixed(1)
                  : '0.0'}
              </Typography>
            </div>
          </>
        );
      case PackFactorCalculationOption.CUSTOM_PACK_FACTOR_SINGLE:
        return (
          <>
            <div className={classes.infoBox}>
              <Typography className={classes.label}>Pack Value</Typography>
              <Typography className={classes.value}>Custom</Typography>
            </div>
            <div className={classes.infoBox}>
              <Typography className={classes.label}>Pack Factor</Typography>
              <Typography className={classes.value}>
                {pack_factor_value ? pack_factor_value.toFixed(3) : '0.000'}
              </Typography>
            </div>
          </>
        );
      case PackFactorCalculationOption.NO_PACK_FACTOR:
      default:
        return (
          <div className={classes.infoBox}>
            <Typography className={classes.label}>Pack Factor</Typography>
            <Typography className={classes.value}>None</Typography>
          </div>
        );
    }
  };

  return (
    <>
      <Paper className={classes.root} elevation={0}>
        <div className={classes.spacer} />
        <div className={classes.infoContainer}>{renderInfo()}</div>
        <IconButton
          className={classes.settingsButton}
          size="small"
          onClick={() => setDialogOpen(true)}
        >
          <SettingsIcon fontSize="small" />
        </IconButton>
      </Paper>
      <InventoryCalculationSettingsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        grain_bin_id={grain_bin_id}
      />
    </>
  );
};

export default withInventoryCalculationParametersHoc(InventoryCalculationInfoBar);
